















































































import {
  defineComponent,
  ref,
  Ref,
  onMounted,
  computed,
  PropType,
  watch,
} from '@vue/composition-api';
import { PermissionsListType, DenylistAllowlistItem } from '@oare/types';
import { DataTableHeader } from 'vuetify';
import sl from '@/serviceLocator';

export default defineComponent({
  props: {
    groupId: {
      type: String,
      required: false,
    },
    search: {
      type: String,
      required: false,
    },
    itemType: {
      type: String as PropType<PermissionsListType>,
      required: true,
    },
    getItems: {
      type: Function as PropType<(groupId?: string) => DenylistAllowlistItem[]>,
      required: true,
    },
    removeItems: {
      type: Function as PropType<(uuids: string[], groupId?: string) => void>,
      required: true,
    },
    addingEditPermissions: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      default: 1,
    },
    rows: {
      type: Number,
      default: 10,
    },
  },
  setup(
    {
      groupId,
      search,
      itemType,
      getItems,
      removeItems,
      addingEditPermissions,
      page,
      rows,
    },
    { emit }
  ) {
    const actions = sl.get('globalActions');

    const listHeaders: Ref<DataTableHeader[]> = ref(
      itemType === 'Image'
        ? [
            { text: 'Name', value: 'name', width: '30%' },
            { text: 'Image', value: 'url', width: '70%' },
          ]
        : [{ text: 'Name', value: 'name' }]
    );

    const items: Ref<DenylistAllowlistItem[]> = ref([]);
    const selectedItems: Ref<DenylistAllowlistItem[]> = ref([]);

    const addUrl = computed(() => {
      if (groupId) {
        return addingEditPermissions
          ? `/admin/add_edit/${itemType.toLowerCase()}s/${groupId}`
          : `/admin/add_allowlist/${itemType.toLowerCase()}s/${groupId}`;
      }
      return `/admin/add_denylist/${itemType.toLowerCase()}s`;
    });
    const itemLink = computed(() =>
      itemType === 'Collection' ? '/collections/name/' : '/epigraphies/'
    );

    const loading = ref(true);
    const confirmRemoveDialog = ref(false);
    const removeItemsLoading = ref(false);
    const searchOptions = ref({
      page: page,
      itemsPerPage: rows,
    });

    watch(
      () => search,
      () => {
        emit('update:search', search);
      }
    );

    watch(
      () => searchOptions.value.page,
      () => {
        emit('update:page', searchOptions.value.page);
      }
    );

    watch(
      () => searchOptions.value.itemsPerPage,
      () => {
        emit('update:rows', searchOptions.value.itemsPerPage);
      }
    );

    const confirmRemoveMessage = computed(() => {
      if (groupId) {
        return `Are you sure you want to remove the following
        ${itemType.toLowerCase()}(s) from this group? Members of this group
        will no longer be able to view this ${itemType.toLowerCase()} if it is denylisted.`;
      } else {
        return `The following ${itemType.toLowerCase()}(s) will be removed
        from the public denylist and will be visible to all users. Are you sure you want to remove them?`;
      }
    });

    onMounted(async () => {
      try {
        if (groupId) {
          items.value = await getItems(groupId);
        } else {
          items.value = await getItems();
        }
        items.value = items.value.sort((a, b) =>
          a.name && b.name ? a.name.localeCompare(b.name) : -1
        );
      } catch (err) {
        actions.showErrorSnackbar(
          `Error loading group ${itemType}(s). Please try again.`,
          err as Error
        );
      } finally {
        loading.value = false;
      }
    });

    const removeListItems = async () => {
      try {
        removeItemsLoading.value = true;
        let uuidsToRemove = selectedItems.value.map(item => item.uuid);
        if (groupId) {
          await removeItems(uuidsToRemove, groupId);
        } else {
          await removeItems(uuidsToRemove);
        }
        items.value = items.value.filter(
          item => !uuidsToRemove.includes(item.uuid)
        );
        actions.showSnackbar(
          `Successfully removed ${itemType.toLowerCase()}(s).`
        );
        selectedItems.value = [];
      } catch (err) {
        actions.showErrorSnackbar(
          `Error removing ${itemType.toLowerCase()}(s). Please try again.`,
          err as Error
        );
      } finally {
        confirmRemoveDialog.value = false;
        removeItemsLoading.value = false;
      }
    };

    return {
      loading,
      items,
      listHeaders,
      selectedItems,
      searchOptions,
      confirmRemoveDialog,
      removeItemsLoading,
      removeListItems,
      addUrl,
      itemLink,
      confirmRemoveMessage,
    };
  },
});
